/* ============================================================
   Teeccino slide cart — in-theme replacement for UpCart.

   Self-contained on purpose: does NOT touch assets/cart-drawer.js or
   assets/cart.js, so Dawn's dormant drawer and this cart cannot break
   each other. Uses /cart/*.js (theme cart), the Section Rendering API
   for re-render, and /recommendations/products for upsells.

   Element name is <teeccino-cart> and the root id is TCCart — neither
   matches UpCart's `cart-drawer[class*=drawer]` / `div[id~=cart-drawer]`
   !important suppressors, so both carts can coexist while we test.
   ============================================================ */

(function () {
  'use strict';

  const SECTION_ID = 'tc-cart';
  const UPSELL_SECTION_ID = 'tc-cart-upsells';
  const DEBOUNCE = 260;

  const cfg = () =>
    window.tcCartConfig || { rewards: [], upsellsEnabled: false, upsellLimit: 10 };

  const money = (cents) => {
    // Server-rendered money is authoritative; this is only for JS-side
    // fallbacks (errors, live-region text).
    const n = (cents / 100).toFixed(2);
    return '$' + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Cart total that counts toward reward tiers. Donation lines (product type
  // "Donation" - the Easy Donation campaign products) are excluded so a gift to
  // charity never unlocks a free product. Mirrors snippets/tc-cart-rewards.liquid.
  const rewardTotal = (cart) =>
    (cart.items || []).reduce(
      (sum, i) =>
        i.product_type === 'Donation' ? sum - (i.final_line_price || 0) : sum,
      cart.total_price || 0
    );

  class TeeccinoCart extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._recPromise = null;
      this._chain = null;
      this._inFlight = 0;
      this._refreshTimer = null;
      this._lastFocus = null;
      this._upsellPid = null;
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));

      document.addEventListener('teeccino:cart-refreshed', () => this.syncBubble());

      if (window.__tcPendingOpen) {
        window.__tcPendingOpen = false;
        this.open();
      }
    }

    /* ---------------- open / close ---------------- */

    open() {
      if (this._open) {
        this.refresh();
        return;
      }
      this._lastFocus = document.activeElement;
      this._open = true;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden', 'tc-cart-open');
      document.documentElement.classList.add('tc-cart-open');
      document.addEventListener('keydown', this.onKeydown);
      this.trap();
      this.refresh();
    }

    close() {
      if (!this._open) return;
      this._open = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden', 'tc-cart-open');
      document.documentElement.classList.remove('tc-cart-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this._lastFocus || null);
      } else if (this._lastFocus && this._lastFocus.focus) {
        this._lastFocus.focus();
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        this.close();
      }
    }

    trap(target) {
      const panel = this.querySelector('.tc-cart__panel');
      if (!panel) return;
      const first =
        target || this.querySelector('.tc-cart__close') || panel;
      if (typeof trapFocus === 'function') {
        trapFocus(panel, first);
      } else if (first.focus) {
        first.focus();
      }
    }

    /* ---------------- events ---------------- */

    onClick(e) {
      const t = e.target;
      this._focusHint = this.describeFocus(t);

      if (t.closest('[data-tc-close]')) {
        e.preventDefault();
        this.close();
        return;
      }

      const qtyBtn = t.closest('[data-tc-qty]');
      if (qtyBtn) {
        e.preventDefault();
        const line = qtyBtn.closest('.tc-line');
        const key = line && line.dataset.key;
        const current = parseInt(line.dataset.qty, 10) || 0;
        const next =
          qtyBtn.dataset.tcQty === 'up' ? current + 1 : Math.max(0, current - 1);
        this.userAction();
        this.mutateLine(key, next, line);
        return;
      }

      if (t.closest('[data-tc-remove]')) {
        e.preventDefault();
        const line = t.closest('.tc-line');
        this.userAction();
        this.mutateLine(line.dataset.key, 0, line);
        return;
      }

      const addBtn = t.closest('[data-tc-add]');
      if (addBtn) {
        e.preventDefault();
        this.userAction();
        this.addFromTile(addBtn);
        return;
      }

      const subBtn = t.closest('[data-tc-subscribe]');
      if (subBtn) {
        e.preventDefault();
        this.userAction();
        this.upgradeLine(subBtn);
        return;
      }

      const navBtn = t.closest('[data-tc-scroll]');
      if (navBtn) {
        e.preventDefault();
        const shelf = navBtn.closest('.tc-shelf');
        const sc = shelf && shelf.querySelector('.tc-shelf__scroller');
        if (!sc) return;
        const step = sc.clientWidth * 0.9;
        sc.scrollBy({
          left: navBtn.dataset.tcScroll === 'next' ? step : -step,
          behavior: 'smooth',
        });
      }
    }

    onChange(e) {
      this._focusHint = this.describeFocus(e.target);
      const freq = e.target.closest('[data-tc-frequency]');
      if (freq) {
        this.userAction();
        this.changeFrequency(freq);
        return;
      }
      const sel = e.target.closest('[data-tc-variant-select]');
      if (!sel) return;
      const tile = sel.closest('.tc-tile');
      const opt = sel.selectedOptions[0];
      const add = tile && tile.querySelector('[data-tc-add]');
      if (!add || !opt) return;
      add.dataset.variantId = sel.value;
      add.disabled = opt.dataset.available === 'false';
      const price = tile.querySelector('[data-tc-tile-price]');
      if (price && opt.dataset.price) price.textContent = opt.dataset.price;
      const cmp = tile.querySelector('[data-tc-tile-compare]');
      if (cmp) cmp.textContent = opt.dataset.compare || '';
    }

    userAction() {
      this.setError('');
    }

    /* ---------------- cart mutations ---------------- */

    async mutateLine(key, quantity, lineEl) {
      if (!key) return;
      if (lineEl) lineEl.classList.add('is-busy');
      const titleEl = lineEl && lineEl.querySelector('.tc-line__title');
      const title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : 'Item';
      this.announce(quantity === 0 ? `Removed ${title} from cart` : `${title}, quantity ${quantity}`);
      try {
        const res = await fetch(`${window.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error(await res.text());
        await this.refresh();
      } catch (err) {
        this.setError(this.errText(err));
        if (lineEl) lineEl.classList.remove('is-busy');
      }
    }

    async addFromTile(btn) {
      const id = btn.dataset.variantId;
      if (!id) return;
      const label = btn.textContent;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'ADDING…';
      try {
        const res = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Add failed');
        this.announce('Added to cart');
        await this.refresh();
      } catch (err) {
        this.setError(this.errText(err));
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
      }
    }

    async upgradeLine(btn) {
      const line = btn.closest('.tc-line');
      const key = line && line.dataset.key;
      const qty = parseInt(line.dataset.qty, 10) || 1;
      const plan = btn.dataset.sellingPlan;
      if (!key || !plan) return;
      btn.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${window.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity: qty,
            selling_plan: Number(plan),
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        this.announce('Switched to Subscribe & Save');
        await this.refresh();
      } catch (err) {
        this.setError(this.errText(err));
        btn.removeAttribute('aria-busy');
      }
    }

    // Switch a subscription line to another delivery frequency in its own
    // selling-plan group. Same cart mutation as upgradeLine — /cart/change.js
    // with the line key, unchanged quantity and the new selling_plan.
    async changeFrequency(select) {
      const line = select.closest('.tc-line');
      const key = line && line.dataset.key;
      const qty = parseInt(line && line.dataset.qty, 10) || 1;
      const plan = select.value;
      if (!key || !plan) return;
      select.disabled = true;
      line.classList.add('is-busy');
      try {
        const res = await fetch(`${window.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty, selling_plan: Number(plan) }),
        });
        if (!res.ok) throw new Error(await res.text());
        const label = select.selectedOptions[0] ? select.selectedOptions[0].textContent.trim() : '';
        this.announce(label ? `Delivery changed to ${label}` : 'Delivery frequency changed');
        await this.refresh();
      } catch (err) {
        this.setError(this.errText(err));
        select.disabled = false;
        line.classList.remove('is-busy');
      }
    }

    /* ---------------- rewards reconciliation ----------------
       Runs BEFORE the re-render so a gift add/remove costs one
       render, not two. Never called from inside the render path.
       ------------------------------------------------------- */

    reconcileRewards() {
      // Keep EVERY tier whose gift variant resolves — including tiers whose
      // threshold is 0, i.e. switched off. A tier at threshold 0 must never
      // GRANT (`total >= 0` is true on every cart), but it must still be SWEPT,
      // because the gift it already handed out is sitting in live carts right now.
      //
      // Excluding threshold-0 tiers here was a real defect, found by QA on
      // 2026-08-28: disarming the Labor Day Gold Scoop tier removed it from the
      // rewards bar and left every already-earned $0.00 scoop in the cart, still
      // checkout-able, indefinitely. The remover could not see a gift it no
      // longer offered. The threshold test now lives in `qualifies` below, where
      // 0 means "never granted" instead of "invisible".
      const tiers = (cfg().rewards || []).filter((t) => t.variantId);
      if (!tiers.length) return Promise.resolve(false);
      // JOIN an in-flight reconcile rather than skipping it. Skipping let a
      // second caller run its own /cart.js read against a cart the first pass
      // had not finished mutating, which produced duplicate gift lines.
      if (this._recPromise) return this._recPromise;
      this._recPromise = this._doReconcile(tiers).finally(() => {
        this._recPromise = null;
      });
      return this._recPromise;
    }

    async _doReconcile(tiers) {
      let changed = false;

      try {
        const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
        if (cart.item_count === 0) {
          // Nothing to reward. Strip any orphaned gift lines.
          for (const t of tiers) {
            const orphan = cart.items.find(
              (i) => String(i.variant_id) === String(t.variantId)
            );
            if (orphan) {
              await this.rawChange(orphan.key, 0);
              changed = true;
            }
          }
          return changed;
        }

        for (const t of tiers) {
          const threshold = Number(t.threshold) || 0;
          let lines = cart.items.filter(
            (i) => String(i.variant_id) === String(t.variantId)
          );

          // Invariant repair FIRST, unconditionally: collapse any duplicate
          // gift lines before deciding whether to add or remove. This makes the
          // "exactly one gift line, ever" assertion self-healing even if a race
          // or a stale render slipped an extra line in.
          while (lines.length > 1) {
            const dupe = lines.pop();
            await this.rawChange(dupe.key, 0);
            changed = true;
          }

          // threshold 0 == tier switched off. Never qualifies, so the
          // `!qualifies && lines.length > 0` branch below sweeps any orphaned
          // line left over from a finished promo.
          const qualifies = threshold > 0 && rewardTotal(cart) >= threshold;

          if (qualifies && lines.length === 0) {
            await this.rawAdd(t.variantId);
            changed = true;
          } else if (!qualifies && lines.length > 0) {
            for (const l of lines) await this.rawChange(l.key, 0);
            changed = true;
          } else if (qualifies && lines.length > 0) {
            if (lines[0].quantity !== 1) {
              await this.rawChange(lines[0].key, 1);
              changed = true;
            }
          }
        }
      } catch (err) {
        // A failed reward reconcile must never block the drawer render.
        console.warn('[tc-cart] reward reconcile failed', err);
      }

      return changed;
    }

    rawAdd(variantId) {
      return fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
    }

    rawChange(key, quantity) {
      return fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
    }

    /* ---------------- render ---------------- */

    refresh() {
      clearTimeout(this._refreshTimer);
      const debounced = new Promise((resolve) => {
        this._refreshTimer = setTimeout(resolve, DEBOUNCE);
      });
      // Every refresh cycle joins one serial chain: reconcile and render for
      // action N always complete before action N+1 reads the cart.
      this._chain = (this._chain || Promise.resolve())
        .then(() => debounced)
        .then(() => this.reconcileRewards())
        .then(() => this.render())
        .catch((err) => console.warn('[tc-cart] refresh failed', err));
      return this._chain;
    }

    async render() {
      this._inFlight += 1;
      const ticket = this._inFlight;
      try {
        const url = `${window.location.pathname}?sections=${SECTION_ID}`;
        const data = await (await fetch(url)).json();
        if (ticket !== this._inFlight) return; // superseded
        const html = data[SECTION_ID];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('teeccino-cart');
        if (!fresh) return;

        const active = document.activeElement;
        const hint = this.describeFocus(active) || this._focusHint || null;
        this._focusHint = null;
        const oldRewards = this.querySelector('.tc-rewards__message');
        const oldRewardsText = oldRewards ? oldRewards.textContent.trim() : '';

        this.innerHTML = fresh.innerHTML;
        this.dataset.itemCount = fresh.dataset.itemCount || '0';
        this.classList.toggle('is-empty', (fresh.dataset.itemCount || '0') === '0');

        this.syncBubble();
        this.hydrateUpsells();
        if (this._open) this.trap(this.findFocusTarget(hint));
        // Speak the action result, plus the rewards line when it changed
        // ("$12 away from free shipping"), so progress isn't silent.
        const newRewards = this.querySelector('.tc-rewards__message');
        const newRewardsText = newRewards ? newRewards.textContent.trim().replace(/\s+/g, ' ') : '';
        this.flushAnnounce(
          this._pendingAnnounce && newRewardsText && newRewardsText !== oldRewardsText.replace(/\s+/g, ' ')
            ? newRewardsText
            : ''
        );
        document.dispatchEvent(
          new CustomEvent('teeccino:cart-refreshed', {
            detail: { itemCount: Number(this.dataset.itemCount || 0) },
          })
        );
        if (window.PUB_SUB_EVENTS && typeof publish === 'function') {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tc-cart' });
          } catch (e) {
            /* noop */
          }
        }
      } catch (err) {
        this.setError(this.errText(err));
      }
    }

    /* ---------------- upsells (server-rendered via /recommendations) ---------------- */

    async hydrateUpsells() {
      const slot = this.querySelector('[data-tc-upsell-slot]');
      if (!slot) return;
      const pid = slot.dataset.productId;
      if (!pid) {
        slot.innerHTML = '';
        return;
      }
      const limit = cfg().upsellLimit || 10;
      try {
        const url = `/recommendations/products?section_id=${UPSELL_SECTION_ID}&product_id=${pid}&limit=${limit}&intent=related`;
        const text = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const shelf = doc.querySelector('[data-tc-upsell-shelf]');
        slot.innerHTML = shelf ? shelf.outerHTML : '';
      } catch (err) {
        slot.innerHTML = '';
      }
    }

    /* ---------------- misc ---------------- */

    syncBubble() {
      const count = Number(this.dataset.itemCount || 0);
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon) return;
      let bubble = icon.querySelector('.cart-count-bubble');
      if (count > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span>';
          icon.appendChild(bubble);
        }
        const num = bubble.querySelector('span[aria-hidden]');
        if (num) num.textContent = count < 100 ? count : '';
      } else if (bubble) {
        bubble.remove();
      }
    }

    setError(msg) {
      if (msg) this._pendingAnnounce = ''; // a failed action must not announce success
      const el = this.querySelector('[data-tc-error]');
      if (el) el.textContent = msg || '';
    }

    // The live region is re-created by every render() (innerHTML swap), which
    // used to wipe a message before a screen reader read it. Queue it instead;
    // render() writes it into the NEW region once the swap has settled.
    announce(msg) {
      this._pendingAnnounce = msg;
    }

    flushAnnounce(extra) {
      const msg = [this._pendingAnnounce, extra].filter(Boolean).join('. ');
      this._pendingAnnounce = '';
      if (!msg) return;
      const el = this.querySelector('[data-tc-live]');
      if (!el) return;
      el.textContent = '';
      setTimeout(() => {
        el.textContent = msg;
      }, 150);
    }

    /* ---------------- focus continuity ----------------
       render() replaces the whole drawer's markup. Record the control the
       shopper was on (by line key / tile / role, never by node), then put focus
       back on its replacement so a keyboard user keeps their place
       (WCAG 2.4.3). Without this, focus jumped to Close after every change.
       ------------------------------------------------- */

    describeFocus(el) {
      if (!el || !this.contains(el) || el === this) return null;
      const ctlSelectors = [
        '[data-tc-qty="up"]',
        '[data-tc-qty="down"]',
        '[data-tc-remove]',
        '[data-tc-frequency]',
        '[data-tc-subscribe]',
        '[data-tc-variant-select]',
        '[data-tc-add]',
        '[data-tc-scroll="prev"]',
        '[data-tc-scroll="next"]',
        '.tc-line__title',
        '.tc-tile__title',
        '.tc-cart__checkout',
        '.tc-cart__close',
      ];
      const ctl = ctlSelectors.find((s) => el.closest(s));
      if (!ctl) return null;
      const line = el.closest('.tc-line');
      const tile = el.closest('.tc-tile');
      const lines = Array.from(this.querySelectorAll('.tc-line'));
      const tileLink = tile && tile.querySelector('a[href]');
      return {
        ctl,
        lineKey: line ? line.dataset.key : null,
        lineIndex: line ? lines.indexOf(line) : -1,
        tileHref: tileLink ? tileLink.getAttribute('href') : null,
      };
    }

    findFocusTarget(hint) {
      if (!hint) return null;
      const usable = (el) => el && !el.disabled && el.offsetParent !== null;
      const firstIn = (root) =>
        root &&
        Array.from(root.querySelectorAll('a[href], button, select, input, [tabindex]:not([tabindex="-1"])')).find(
          (el) => usable(el) && el.getAttribute('tabindex') !== '-1'
        );
      const pickInLine = (line) => {
        if (!line) return null;
        const same = line.querySelector(hint.ctl);
        if (usable(same)) return same;
        // e.g. "Upgrade to Subscribe" became the frequency picker
        const freq = line.querySelector('[data-tc-frequency]');
        if (usable(freq)) return freq;
        return firstIn(line);
      };
      if (hint.lineKey) {
        const lines = Array.from(this.querySelectorAll('.tc-line'));
        const line = lines.find((l) => l.dataset.key === hint.lineKey);
        if (line) return pickInLine(line);
        // Key changed (a selling-plan change re-keys the line) or the line was
        // removed: use the line now in that position, else the one above.
        if (hint.ctl === '[data-tc-remove]' || hint.ctl === '[data-tc-qty="down"]') {
          return firstIn(lines[hint.lineIndex] || lines[hint.lineIndex - 1]) || null;
        }
        return pickInLine(lines[hint.lineIndex] || lines[hint.lineIndex - 1]);
      }
      if (hint.tileHref) {
        const link = Array.from(this.querySelectorAll('.tc-tile a[href]')).find(
          (a) => a.getAttribute('href') === hint.tileHref
        );
        const tile = link && link.closest('.tc-tile');
        if (tile) {
          const same = tile.querySelector(hint.ctl);
          if (usable(same)) return same;
          return firstIn(tile);
        }
        // The added product left the shelf: go to the newest cart line.
        const lines = this.querySelectorAll('.tc-line');
        return firstIn(lines[lines.length - 1]) || null;
      }
      const el = this.querySelector(hint.ctl);
      return usable(el) ? el : null;
    }

    errText(err) {
      const raw = (err && err.message) || String(err || '');
      try {
        const parsed = JSON.parse(raw);
        return parsed.description || parsed.message || raw;
      } catch (e) {
        return raw.length > 160
          ? (window.cartStrings && window.cartStrings.error) || 'Something went wrong.'
          : raw;
      }
    }
  }

  if (!customElements.get('teeccino-cart')) {
    customElements.define('teeccino-cart', TeeccinoCart);
  }

  /* ---------------- public API + UpCart shim ---------------- */

  const find = () => document.querySelector('teeccino-cart');

  window.teeccinoOpenCart = function () {
    const el = find();
    if (el && typeof el.open === 'function') el.open();
    else window.__tcPendingOpen = true;
  };

  window.teeccinoCloseCart = function () {
    const el = find();
    if (el && typeof el.close === 'function') el.close();
  };

  // Utility for other theme JS: money formatter used in error paths.
  window.teeccinoCartMoney = money;
})();
